import type { FlexProps, Gap, GridProps, Margin } from '$types/layout';

/**
 * Default gap to be parsed into CSS as a variable using @function parseGap()
 */
export const DefaultGap = {
  column: '0',
  row: '0'
};
/**
 * parses @param gap for usage into CSS / SCSS as a variable directly
 * @param {string | Gap} gap - gap to be parsed into a string
 * @param {Gap} defaultGap - default gap properties if some properties of @param gap are omitted, defaults to @const DefaultGap
 * @returns parsed fap to be directly used inside CSS
 */
export function parseGap(gap: string | Gap, defaultGap: Gap = DefaultGap): string {
  if (typeof gap === 'object') {
    gap = {
      ...defaultGap,
      ...gap
    };
    return `${gap.row} ${gap.column}`;
  }
  return gap;
}

/**
 * Default margin to be parsed into CSS as a variable using @function parseMargin()
 */
export const DefaultMargin: Margin = {
  top: '0',
  right: '0',
  bottom: '0',
  left: '0'
};

/**
 * parses @param margin for usage into CSS / SCSS as a variable directly
 * @param {string | Margin} margin - margin to be parsed into a string
 * @param {Margin} defaultMargin - default margins to include if some properties of @param margin are omitted, defaults to @const DefaultMargin
 * @returns parsed margin to be directly used inside CSS
 */
export function parseMargin(
  margin: string | Margin,
  defaultMargin: Margin = DefaultMargin
): string {
  if (typeof margin === 'object') {
    margin = {
      ...defaultMargin,
      ...margin
    };
    return `${margin.top} ${margin.right} ${margin.bottom} ${margin.left}`;
  }
  return margin;
}

/**
 * Default FlexProps to be parsed into CSS as a variable using @function parseFlexProps()
 */
export const DefaultFlexProps: FlexProps = {
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  direction: 'row',
  wrap: 'nowrap',
  width: 'fit-content',
  height: 'fit-content',
  margin: '0'
};

/**
 * parses @param flexProps for usage into CSS / SCSS as a variable directly
 * @param {FlexProps} flexProps - flexProps to be parsed into a string
 * @param {FlexProps} defaultFlexProps - default flexProps to include if some properties of @param flexProps are omitted, defaults to @const DefaultFlexProps
 * @returns parsed flexProps to be directly used inside CSS
 */
export function parseFlexProps(
  flexProps: FlexProps,
  defaultFlexProps: FlexProps = DefaultFlexProps
): FlexProps {
  flexProps = {
    ...defaultFlexProps,
    ...flexProps
  };
  if (flexProps.margin) flexProps.margin = parseMargin(flexProps.margin);
  if (flexProps.gap) flexProps.gap = parseGap(flexProps.gap);
  return flexProps;
}

/**
 * parses @param flexProps for usage in plain CSS
 * @param {FlexProps} flexProps - flexProps to be parsed into a string
 * @param {FlexProps} defaultFlexProps - default flexProps to include if some properties of @param flexProps are omitted, defaults to @const DefaultFlexProps
 * @returns parsed flexProps to be directly used inside CSS as a string
 */
export function getFlexPropsAsString(
  flexProps: FlexProps,
  defaultFlexProps: FlexProps = DefaultFlexProps
): string {
  flexProps = parseFlexProps(flexProps, defaultFlexProps);
  return `
    --direction: ${flexProps.direction};
    --wrap: ${flexProps.wrap};
    --justifyContent: ${flexProps.justifyContent};
    --alignItems: ${flexProps.alignItems};
    --gap: ${flexProps.gap};
    --width: ${flexProps.width};
    --height: ${flexProps.height};
    --margin: ${flexProps.margin};
  `;
}

/**
 * Default GridProps to be parsed into CSS as a variable using @function parseGridProps()
 */
export const DefaultGridProps: GridProps = {
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  templateColumns: 'unset',
  templateRows: 'unset',
  margin: '0',
  width: 'unset',
  height: 'unset',
  autoFlow: 'row'
};

/**
 * parses @param gridProps for usage into CSS / SCSS as a variable directly
 * @param {GridProps} gridProps - gridProps to be parsed into a string
 * @param {GridProps} defaultGridProps - default gridProps to include if some properties of @param gridProps are omitted, defaults to @const DefaultGridProps
 * @returns parsed gridProps to be directly used inside CSS
 */
export function parseGridProps(
  gridProps: GridProps,
  defaultGridProps: GridProps = DefaultGridProps
): GridProps {
  gridProps = {
    ...defaultGridProps,
    ...gridProps
  };
  if (gridProps.margin) gridProps.margin = parseMargin(gridProps.margin);
  if (gridProps.gap) gridProps.gap = parseGap(gridProps.gap);
  return gridProps;
}

/**
 * parses @param gridProps for usage in plain CSS
 * @param {GridProps} gridProps - gridProps to be parsed into a string
 * @param {GridProps} defaultGridProps - default gridProps to include if some properties of @param gridProps are omitted, defaults to @const DefaultGridProps
 * @returns parsed gridProps to be directly used inside CSS as a string
 */
export function getGridPropsAsString(
  gridProps: GridProps,
  defaultGridProps: GridProps = DefaultGridProps
): string {
  gridProps = parseGridProps(gridProps, defaultGridProps);
  return `
    --justifyContent: ${gridProps.justifyContent};
    --alignItems: ${gridProps.alignItems};
    --gap: ${gridProps.gap};
    --templateColumns: ${gridProps.templateColumns};
    --templateRows: ${gridProps.templateRows};
    --margin: ${gridProps.margin};
    --width: ${gridProps.width};
    --height: ${gridProps.height};
    --autoFlow: ${gridProps.autoFlow};
  `;
}
